import { AppointmentType, HoldReleaseReason, ProviderScheduleBlockState, ProviderScheduleEntryKind } from 'constants/'
import { addMinutes, differenceInMilliseconds, isAfter, isBefore, parseISO } from 'date-fns'
import { Customer, CustomerAppointment, ProviderScheduleEntry } from 'types'
import { indexBy, prop } from '../fp'

type BlockStatus = 'held' | 'released-open' | 'released-closed'

interface ScheduleBlock {
  appointmentType: AppointmentType
  customerSharedAt: string
  durationInMinutes: number
  id: string
  kind: 'block'
  poolGid: string
  releaseReason: HoldReleaseReason | null
  startDate: string
  state: ProviderScheduleBlockState
}

interface ScheduleAppointment {
  appointment: CustomerAppointment
  kind: 'appointment'
}

type ScheduleEntry = ScheduleAppointment | ScheduleBlock

interface AwaitingAccessAppointment {
  durationInMinutes: number
  startDate: string
}

const ONE_HOUR_IN_MILLISECONDS = 60 * 60_000

const toScheduleEntry = (
  entry: ProviderScheduleEntry,
  customersBySalesforceId: Record<string, Customer>
): ScheduleEntry | undefined => {
  if (entry.kind === ProviderScheduleEntryKind.BLOCK) {
    const { appointment_type, customer_shared_at, pool_gid, release_reason, state } = entry.block

    return {
      appointmentType: appointment_type,
      customerSharedAt: customer_shared_at,
      durationInMinutes: entry.duration_in_minutes,
      id: `${pool_gid}-${entry.start_date}`,
      kind: 'block',
      poolGid: pool_gid,
      releaseReason: release_reason,
      startDate: entry.start_date,
      state,
    }
  }

  if (entry.kind !== ProviderScheduleEntryKind.APPOINTMENT) {
    return undefined
  }

  const gid = entry.appointment.customer?.gid

  if (gid === undefined || !Object.hasOwn(customersBySalesforceId, gid)) {
    return undefined
  }

  const customer = customersBySalesforceId[gid]

  return { appointment: { ...entry.appointment, customer }, kind: 'appointment' }
}

const toScheduleEntries = (
  entries: ProviderScheduleEntry[],
  customersBySalesforceId: Record<string, Customer>
): ScheduleEntry[] =>
  entries.flatMap((entry) => {
    const scheduleEntry = toScheduleEntry(entry, customersBySalesforceId)

    return scheduleEntry ? [scheduleEntry] : []
  })

const toAwaitingAccess = (
  entries: ProviderScheduleEntry[],
  customersBySalesforceId: Record<string, Customer>
): AwaitingAccessAppointment[] =>
  entries.flatMap((entry) => {
    if (entry.kind !== ProviderScheduleEntryKind.APPOINTMENT || !entry.is_pooled) {
      return []
    }

    const gid = entry.appointment.customer?.gid

    return gid === undefined || Object.hasOwn(customersBySalesforceId, gid)
      ? []
      : [{ durationInMinutes: entry.duration_in_minutes, startDate: entry.start_date }]
  })

const isBlock = (entry: ScheduleEntry): entry is ScheduleBlock => entry.kind === 'block'

const isHeld = (block: ScheduleBlock) => block.state === ProviderScheduleBlockState.HELD

const toBlockStatus = (block: ScheduleBlock, now: Date): BlockStatus => {
  if (isHeld(block)) {
    return 'held'
  }

  return isBefore(now, parseISO(block.customerSharedAt)) ? 'released-open' : 'released-closed'
}

const nextRefreshAt = (entries: ScheduleEntry[], now: Date): Date | undefined =>
  entries
    .filter(isBlock)
    .map((block) => parseISO(block.customerSharedAt))
    .filter((customerSharedAt) => isAfter(customerSharedAt, now))
    .sort((a, b) => a.getTime() - b.getTime())[0]

const hasEnded = ({ durationInMinutes, startDate }: AwaitingAccessAppointment, now: Date): boolean =>
  !isAfter(addMinutes(parseISO(startDate), durationInMinutes), now)

const isSharingNow = (block: ScheduleBlock, now: Date): boolean =>
  isHeld(block) && !isAfter(parseISO(block.customerSharedAt), now)

const canReleaseHold = (block: ScheduleBlock, now: Date): boolean => isHeld(block) && !isSharingNow(block, now)

const isAwaitingCustomer = (entries: ScheduleEntry[], now: Date): boolean =>
  entries.filter(isBlock).some((block) => isSharingNow(block, now) && !hasEnded(block, now))

const isAwaitingAccess = (appointments: AwaitingAccessAppointment[], now: Date): boolean =>
  appointments.some((appointment) => !hasEnded(appointment, now))

interface SchedulePageData {
  customers: Customer[]
  entries: ProviderScheduleEntry[]
}

const isPageAwaitingCustomer = ({ customers, entries }: SchedulePageData, now: Date): boolean => {
  const customersBySalesforceId = indexBy(customers, prop('salesforceId'))

  return (
    isAwaitingCustomer(toScheduleEntries(entries, customersBySalesforceId), now) ||
    isAwaitingAccess(toAwaitingAccess(entries, customersBySalesforceId), now)
  )
}

const isWithinLastHour = (block: ScheduleBlock, now: Date): boolean => {
  const untilShared = differenceInMilliseconds(parseISO(block.customerSharedAt), now)

  return isHeld(block) && untilShared > 0 && untilShared <= ONE_HOUR_IN_MILLISECONDS
}

const toLatestNow = (now: Date): Date => new Date(Math.max(now.getTime(), Date.now()))

export {
  canReleaseHold,
  isAwaitingAccess,
  isAwaitingCustomer,
  isPageAwaitingCustomer,
  isSharingNow,
  isWithinLastHour,
  nextRefreshAt,
  toAwaitingAccess,
  toBlockStatus,
  toLatestNow,
  toScheduleEntries,
}
export type {
  AwaitingAccessAppointment,
  BlockStatus,
  ScheduleAppointment,
  ScheduleBlock,
  ScheduleEntry,
  SchedulePageData,
}
